import { useState, useEffect, useMemo } from "react";
import apiService from "../services/apiService";
import { getTimezoneNames } from "../utils/timezones";
import { handleUpdateError, handleCreateError, handleDeleteError } from "../utils/errorHandler";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  TableContainer,
  TableCell,
  TableBody,
  TableHead,
  Paper,
  Table,
  TableRow,
  Typography,
  Alert,
  Box,
  Autocomplete,
  FormControl,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as EditIcon } from "../Icons/Edit.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";

const ScheduleForm = ({ open, handleClose, selectedBJob }) => {
  // Get timezones from centralized utility
  const timezones = useMemo(() => getTimezoneNames(), []);

  const [formData, setFormData] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    environment_name: "",
    app_id: "",
    application_name: "",
    job_id: "",
    jobs_script: "",
    time_zone: "",
    prev_start_scheduled_time: "",
    start_scheduled_time: "",
    end_scheduled_time: "",
    interval_mins: "",
    rh_eom: "",
    rh_prev_eom: "",
    reference_1: "",
  });

  const [selectedDays, setSelectedDays] = useState([]);

  const [errors, setErrors] = useState({});
  const [dbSchedules, setDbSchedules] = useState([]);
  const [isEditingDbSchedule, setIsEditingDbSchedule] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [loading, setLoading] = useState(false);

  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    jobs_script: "Job",
    time_zone: "Time Zone",
    start_scheduled_time: "Start Time",
    end_scheduled_time: "End Time",
    interval_mins: "Interval (min)",
    rh_eom: "RH EOM",
    rh_prev_eom: "Prev EOM",
    reference_1: "Frequency",
  };

  const columns = Object.keys(columnLabels);

  const daysOfWeek = [
    { label: "Mon", value: "Mon" },
    { label: "Tue", value: "Tue" },
    { label: "Wed", value: "Wed" },
    { label: "Thu", value: "Thu" },
    { label: "Fri", value: "Fri" },
    { label: "Sat", value: "Sat" },
    { label: "Sun", value: "Sun" },
  ];

  // Initialize formData when dialog opens or selectedBJob changes
  useEffect(() => {
    if (open && selectedBJob) {
      setFormData((prev) => ({
        ...prev,
        cl_id: selectedBJob.cl_id || "",
        client_name: selectedBJob.client_name || "",
        env_id: selectedBJob.env_id || "",
        environment_name: selectedBJob.environment_name || "",
        app_id: selectedBJob.app_id || "",
        application_name: selectedBJob.application_name || "",
        job_id: selectedBJob.job_id || "",
        jobs_script: selectedBJob.jobs_script || "",
      }));
      fetchDbSchedules(selectedBJob.job_id);
    }
  }, [open, selectedBJob]);

  // Sync selectedDays with formData.reference_1
  useEffect(() => {
    if (formData.reference_1) {
      const days = formData.reference_1.split(',').filter(d => d.trim());
      setSelectedDays(days);
    } else {
      setSelectedDays([]);
    }
  }, [formData.reference_1]);

  // Update formData.reference_1 when selectedDays changes
  useEffect(() => {
    const frequencyString = selectedDays.join(',');
    setFormData(prev => ({
      ...prev,
      reference_1: frequencyString
    }));
  }, [selectedDays]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditingDbSchedule && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditingDbSchedule]);

  const fetchDbSchedules = async (job_id) => {
    setLoading(true);
    try {
      const data = await apiService.get(`/schedule/${job_id}`);
      setDbSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.message && error.message.includes("404")) {
        setDbSchedules([]);
        return;
      }
      console.error("Failed to fetch DB schedules", error);
      setDbSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (data) => {
    const newErrors = {};

    // Required field validation
    const requiredFields = [
      { key: "time_zone", label: "Time Zone" },
      { key: "start_scheduled_time", label: "Start Time" },
      { key: "end_scheduled_time", label: "End Time" },
      { key: "interval_mins", label: "Interval Minutes" },
      { key: "rh_eom", label: "RH EOM" },
      { key: "rh_prev_eom", label: "Prev EOM" },
    ];

    requiredFields.forEach(({ key, label }) => {
      const value = data[key];
      if (key === "rh_eom" || key === "rh_prev_eom" || key === "interval_mins") {
        if (value === null || value === undefined || value === "") {
          newErrors[key] = `${label} is required`;
        }
      } else {
        if (!value || value.toString().trim() === "") {
          newErrors[key] = `${label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      cl_id: selectedBJob.cl_id || "",
      client_name: selectedBJob.client_name || "",
      env_id: selectedBJob.env_id || "",
      environment_name: selectedBJob.environment_name || "",
      app_id: selectedBJob.app_id || "",
      application_name: selectedBJob.application_name || "",
      job_id: selectedBJob.job_id || "",
      jobs_script: selectedBJob.jobs_script || "",
      time_zone: "",
      prev_start_scheduled_time: "",
      start_scheduled_time: "",
      end_scheduled_time: "",
      interval_mins: "",
      rh_eom: "",
      rh_prev_eom: "",
      reference_1: "",
    });
    setSelectedDays([]);
    setErrors({});
    setOriginalFormData({});
    setIsFormChanged(false);
    setIsEditingDbSchedule(false);
    handleClose();
  };

  const handleSubmit = async () => {
    // Clear previous errors before validation
    setErrors({});
    
    if (!validateForm(formData)) return;

    try {
      if (isEditingDbSchedule) {
        // Update existing schedule
        await apiService.put(`/schedule/${formData.job_id}`, formData);
        setSuccessMessage("Schedule updated successfully!");
      } else {
        // Add new schedule
        await apiService.post("/schedule", [formData]);
        setSuccessMessage("Schedule added successfully!");
      }

      setTimeout(() => setSuccessMessage(""), 3000);
      resetForm();
      fetchDbSchedules(selectedBJob.job_id);
    } catch (error) {
      console.error("Error saving schedule:", error);
      const errorMessage = isEditingDbSchedule
        ? handleUpdateError(error, "Schedule")
        : handleCreateError(error, "Schedule");
      setSuccessMessage(errorMessage);
      setTimeout(() => setSuccessMessage(""), 5000);
    }
  };

  const handleDbEdit = (row) => {
    const editFormData = {
      cl_id: row.cl_id || "",
      client_name: row.client_name || "",
      env_id: row.env_id || "",
      environment_name: row.environment_name || "",
      app_id: row.app_id || "",
      application_name: row.application_name || "",
      job_id: row.job_id || "",
      jobs_script: row.jobs_script || "",
      time_zone: row.time_zone || "",
      prev_start_scheduled_time: row.start_scheduled_time || "",
      start_scheduled_time: row.start_scheduled_time || "",
      end_scheduled_time: row.end_scheduled_time || "",
      interval_mins: row.interval_mins || "",
      rh_eom: row.rh_eom || "",
      rh_prev_eom: row.rh_prev_eom || "",
      reference_1: row.reference_1 || "",
    };

    setFormData(editFormData);
    setOriginalFormData({ ...editFormData });
    setIsEditingDbSchedule(true);
    setIsFormChanged(false);
  };

  const combinedSchedules = [...dbSchedules];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {isEditingDbSchedule
          ? `Edit Schedule – ${formData.start_scheduled_time || ""}`
          : `Schedule Job – ${selectedBJob?.jobs_script || selectedBJob?.job_id || ""}`}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {successMessage && (
          <Alert
            severity={
              successMessage.includes("successfully") ||
              successMessage.includes("Activated") ||
              successMessage.includes("Deactivated")
                ? "success"
                : "error"
            }
            sx={{ mb: 2 }}
          >
            {successMessage}
          </Alert>
        )}

        <ThemeProvider theme={theme}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 1,
              mt: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Client"
              value={formData.client_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Environment"
              value={formData.environment_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Application"
              value={formData.application_name}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Job"
              value={formData.jobs_script}
              disabled
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <FormControl fullWidth size="small">
              <Autocomplete
                options={timezones}
                value={formData.time_zone || null}
                onChange={(event, newValue) => {
                  handleChange({
                    target: { name: "time_zone", value: newValue || "" },
                  });
                }}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.75rem",
                      minHeight: "32px",
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Time Zone"
                    required
                    error={Boolean(errors.time_zone)}
                    helperText={errors.time_zone || ""}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 35,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Start Time"
              name="start_scheduled_time"
              type="time"
              value={formData.start_scheduled_time}
              onChange={handleChange}
              required
              error={Boolean(errors.start_scheduled_time)}
              helperText={errors.start_scheduled_time || ""}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="End Time"
              name="end_scheduled_time"
              type="time"
              value={formData.end_scheduled_time}
              onChange={handleChange}
              required
              error={Boolean(errors.end_scheduled_time)}
              helperText={errors.end_scheduled_time || ""}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Interval (min)"
              name="interval_mins"
              type="number"
              value={formData.interval_mins}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              error={Boolean(errors.interval_mins)}
              helperText={errors.interval_mins || ""}
              inputProps={{ min: 0 }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <FormControl fullWidth size="small">
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={daysOfWeek}
                getOptionLabel={(option) => option.label === "Mon" ? "Monday" : 
                                           option.label === "Tue" ? "Tuesday" :
                                           option.label === "Wed" ? "Wednesday" :
                                           option.label === "Thu" ? "Thursday" :
                                           option.label === "Fri" ? "Friday" :
                                           option.label === "Sat" ? "Saturday" : "Sunday"}
                value={daysOfWeek.filter(day => selectedDays.includes(day.value))}
                onChange={(event, newValue) => {
                  setSelectedDays(newValue.map(day => day.value));
                }}
                renderOption={(props, option, { selected }) => {
                  const allSelected = selectedDays.length === daysOfWeek.length;
                  
                  return (
                    <>
                      {option.value === "Mon" && (
                        <li
                          key="select-all"
                          onClick={() => {
                            if (allSelected) {
                              setSelectedDays([]);
                            } else {
                              setSelectedDays(daysOfWeek.map(d => d.value));
                            }
                          }}
                          style={{
                            padding: "6px 16px",
                            cursor: "pointer",
                            borderBottom: "1px solid #e0e0e0",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={selectedDays.length > 0 && selectedDays.length < daysOfWeek.length}
                            sx={{ marginRight: 1 }}
                          />
                          Select All
                        </li>
                      )}
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          sx={{ marginRight: 1 }}
                        />
                        {option.label === "Mon" ? "Monday" : 
                         option.label === "Tue" ? "Tuesday" :
                         option.label === "Wed" ? "Wednesday" :
                         option.label === "Thu" ? "Thursday" :
                         option.label === "Fri" ? "Friday" :
                         option.label === "Sat" ? "Saturday" : "Sunday"}
                      </li>
                    </>
                  );
                }}
                renderTags={(value) => (
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    {value.map(option => option.value).join(", ")}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Frequency"
                    
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: 35,
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                      "& .MuiAutocomplete-input": {
                        minWidth: "30px !important",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.75rem",
                      minHeight: "32px",
                    },
                  },
                }}
              />
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="RH EOM"
              name="rh_eom"
              type="number"
              value={formData.rh_eom}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              error={Boolean(errors.rh_eom)}
              helperText={errors.rh_eom || ""}
              inputProps={{ min: 0 }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Prev EOM"
              name="rh_prev_eom"
              type="number"
              value={formData.rh_prev_eom}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              error={Boolean(errors.rh_prev_eom)}
              helperText={errors.rh_prev_eom || ""}
              inputProps={{ min: 0 }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            
          </Box>
        </ThemeProvider>

        <DialogActions>
          <Button onClick={resetForm}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isEditingDbSchedule && !isFormChanged}
          >
            {isEditingDbSchedule ? "Update" : "Add"}
          </Button>
        </DialogActions>

        <ThemeProvider theme={Table_theme}>
        {combinedSchedules.length > 0 && (
          <Box sx={{ mt: 2 }}>
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: "50vh" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Actions</TableCell>
                        {columns.map((col) => (
                          <TableCell key={col}>{columnLabels[col]}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 1}
                            sx={{
                              position: "sticky",
                              left: 0,
                              textAlign: "left",
                              padding: "20px",
                              backgroundColor: "white",
                              zIndex: 1,
                            }}
                          >
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : combinedSchedules.length > 0 ? (
                        combinedSchedules.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Box display="flex" gap={1} flexWrap="nowrap">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDbEdit(row)}
                                  sx={{ color: "primary.main" }}
                                  title="Edit"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setDeleteRow(row);
                                    setOpenDeleteDialog(true);
                                  }}
                                  sx={{ color: "error.main" }}
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                            {columns.map((col) => (
                              <TableCell key={col}>
                                {col === 'client_name' && (row.client_name || row.cl_id || "NA")}
                                {col === 'environment_name' && (row.environment_name || row.env_id || "NA")}
                                {col === 'application_name' && (row.application_name || row.app_id || "NA")}
                                {col === 'jobs_script' && (row.jobs_script || row.job_id || "NA")}
                                {col === 'time_zone' && (row.time_zone || "NA")}
                                {col === 'start_scheduled_time' && (row.start_scheduled_time || "NA")}
                                {col === 'end_scheduled_time' && (row.end_scheduled_time || "NA")}
                                {col === 'interval_mins' && (row.interval_mins || "NA")}
                                {col === 'rh_eom' && (row.rh_eom || "NA")}
                                {col === 'rh_prev_eom' && (row.rh_prev_eom || "NA")}
                                {col === 'reference_1' && (row.reference_1 || "NA")}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={columns.length + 1}
                            sx={{
                              position: "sticky",
                              left: 0,
                              textAlign: "left",
                              padding: "20px",
                              backgroundColor: "white",
                              zIndex: 1,
                            }}
                          >
                            No schedules found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
          </Box>
        )}
        </ThemeProvider>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDeleteDialog(false);
            setDeleteRow(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to delete Schedule for Job{" "}
              <strong>{deleteRow?.jobs_script || deleteRow?.job_id}</strong> at{" "}
              <strong>{deleteRow?.start_scheduled_time}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDeleteDialog(false);
                setDeleteRow(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await apiService.delete(
                    `/schedule/${deleteRow.start_scheduled_time}?cl_id=${deleteRow.cl_id}&env_id=${deleteRow.env_id}&app_id=${deleteRow.app_id}&job_id=${deleteRow.job_id}`,
                  );

                  setOpenDeleteDialog(false);
                  setDeleteRow(null);
                  fetchDbSchedules(deleteRow.job_id);
                  setSuccessMessage("Schedule deleted successfully!");
                  setTimeout(() => setSuccessMessage(""), 3000);
                } catch (error) {
                  console.error("Delete failed:", error);
                  setOpenDeleteDialog(false);
                  setDeleteRow(null);

                  const errorMessage = handleDeleteError(error, "Schedule");
                  setSuccessMessage(errorMessage);
                  setTimeout(() => setSuccessMessage(""), 5000);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleForm;
